import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useExpandAnimation, useAnimatedPresence } from '../hooks/useExpandAnimation';
import { ACTIONABLE_CHEVRON_CLASS, DEFAULT_CHEVRON_CLASS } from '../constants/rowStyles';
import { TAP_TARGET_PHONE } from '../constants/buttons';

/**
 * THE expandable-table-row parts: the chevron, the expanded row's frame, and
 * the full-width panel `<tr>` that holds the expanded content.
 *
 * 🔀 EXTRACTED 2026-09-27, VERBATIM, out of `components/OrderTableView.js` so
 * the hires Table (`components/hires/HireTableView.js`) expands a hire into the
 * SAME panel the order tables render — owner: *"no need to duplicate much…
 * Its only a differnt page 'Hires' with differnt named columns and tabs"*.
 * `OrderTableRow` renders these byte-identically to before (the Department
 * Orders / My Orders DOM goldens are the proof); the comments travelled with
 * the code, so "this cell" / "the shell" below still mean the order table's.
 *
 * 🔑 RESOLVES NOTHING. The chevron takes `actionable` from its caller and the
 * panel takes a finished `children` node — the `OrderTableView.js` contract.
 */

// The expanded summary row must tint its CELLS, not the row: a <tr>
// background is overridden by the table/parent background. `[&>td]` is the
// documented selector for this (CLAUDE.md, "Expandable Table Rows"), and
// the hover step has to be `[&:hover>td]` (tr:hover>td) rather than
// `hover:[&>td]` (>td:hover), or only the cell under the pointer lights up.
export const EXPANDED_ROW_FRAME =
  '[&>td]:bg-gray-100 dark:[&>td]:bg-slate-700 ' +
  '[&:hover>td]:bg-gray-200 dark:[&:hover>td]:bg-slate-600 ' +
  '[&>td]:border-t-2 [&>td]:border-t-blue-400 dark:[&>td]:border-t-blue-500';

/**
 * THE "Editing this row" bar of a row edited IN PLACE — the strip directly
 * under the editing row that carries its label, the amber unsaved chip and its
 * Save / Discard (Cancel). Extracted 2026-10-25, VERBATIM, from the Gag
 * Breakdown's `renderRowEditBar` (`components/gags/GagGroupTable.js`) so the
 * Manage Budgets tables wear the same bar (owner: *"can you not just use the
 * same layout style?"*). The chip prepends the caller's pill shape
 * (`ui/StatusPill#statusPillClass`). The Breakdown's DOM is byte-identical.
 */
export const ROW_EDIT_BAR_CLASS =
  'flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-600 bg-blue-50/60 dark:bg-blue-900/20';
export const ROW_EDIT_BAR_LABEL_CLASS = 'text-xs font-medium text-gray-600 dark:text-gray-300';
export const ROW_EDIT_UNSAVED_CHIP_CLASS = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

/**
 * THE expand/collapse chevron BUTTON of a row edited in place — the Gag
 * Breakdown's desktop chevron, moved VERBATIM out of `GagGroupTable.js`
 * (2026-10-25) so the Manage Budgets tables expand from the same control.
 * The caller names its noun (`expandLabel` / `collapseLabel`).
 *
 * A real <button> owns the toggle so keyboard users get Enter/Space + focus
 * for free; the row around it stays clickable for mouse, and stopPropagation
 * keeps this click from also firing that handler (no double-toggle). `onToggle`
 * is the caller's dirty-guarded toggle, so keyboard activation respects it too.
 *
 * `onToggle` omitted = a non-interactive row: renders the muted static glyph.
 */
export function RowToggleChevron({ expanded, onToggle, className = '', expandLabel, collapseLabel, testId }) {
  const glyph = (
    <span
      className={`inline-block text-xs transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
    >
      &#9654;
    </span>
  );
  if (!onToggle) {
    // Static glyph — never rotates (a non-interactive gag can't expand).
    return (
      <span className={`inline-flex items-center justify-center text-gray-300 dark:text-gray-600 text-xs ${className}`.trim()}>
        &#9654;
      </span>
    );
  }
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={expanded ? collapseLabel : expandLabel}
      data-testid={testId}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={`inline-flex items-center justify-center rounded text-gray-400 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${TAP_TARGET_PHONE} ${className}`.trim()}
    >
      {glyph}
    </button>
  );
}

/**
 * THE desktop panel row under a row edited in place — the Gag Breakdown's,
 * moved VERBATIM out of `GagGroupTable.js` (2026-10-25). Mount it while the
 * panel is open OR closing; `closing` eases its height to 0, `fadeClass` is
 * the caller's `opacity-0` / `opacity-100`. `panelWidth` is the measured
 * scrollport (`null` before the first measurement), less `insetPx` (the
 * Breakdown's group card has a 1px border each side, so it passes 2).
 * `children`: the "Editing this row" bar first, then the details.
 */
export function InlineEditPanelRow({ colSpan, closing, fadeClass, panelWidth, insetPx = 0, testId, children }) {
  return (
    <tr onClick={(e) => e.stopPropagation()} className="!border-0" data-pan-skip="">
      {/* min-w-0 lets this full-span cell shrink below its
          content's intrinsic width (auto table-layout sizes
          cells to content) — so the wide Gantt inside the
          panel scrolls within its OWN container instead of
          stretching the cell → table → page. */}
      <td colSpan={colSpan} className="p-0 min-w-0" data-testid={testId}>
        {/* Closing ALSO animates the panel's height to 0
            (grid-rows 1fr→0fr) alongside the opacity
            fade, so the document shrinks smoothly over
            200ms instead of dropping the full panel
            height in one frame at removal — a huge
            instant shrink is what made the browser clamp
            the scroll with a visible jump. overflow-
            hidden only while closing: an always-on clip
            would break the sticky panel pinning. */}
        <div
          className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
            closing ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          } bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-600 border-b-2 border-b-blue-400 dark:border-b-blue-500 shadow-sm ${fadeClass}`}
        >
          <div className={`min-h-0 min-w-0 ${closing ? 'overflow-hidden' : ''}`}>
          {/* Pin the panel to the visible scrollport — the
              table itself is wider than the viewport. Width
              = the measured content width (full page width
              minus padding/sidebar, minus `insetPx`). The
              old min(72rem, 100vw-2rem) cap is the fallback
              for the pre-measure first paint only. */}
          <div
            className={`sticky left-0 max-w-full ${panelWidth == null ? 'w-[min(72rem,calc(100vw_-_2rem))]' : ''}`}
            style={panelWidth != null ? { width: panelWidth - insetPx } : undefined}
          >
            {children}
          </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

/**
 * The "yours to action" signal on a table row — the CHEVRON, not the row.
 *
 * Product owner, 2026-08-08: *"The green border on items awaiting attention is
 * too glaring … Can we try changing the colour of the expand arrow to green for
 * items requiring attention"*. So the green rectangle that used to be drawn
 * around the row (`ACTIONABLE_ROW_OUTLINE`, deleted with this change) is gone
 * and the meaning moved onto the 16px glyph in the first cell. The colour, the
 * wording, the contrast measurements and the WCAG 1.4.1 reasoning all live with
 * the constants in `constants/rowStyles.js`, beside the values they justify —
 * read that before changing a shade. The CARD layout does the same thing with
 * the same strings, so the two layouts cannot disagree about the same order.
 *
 * ── The predicate does NOT live here ─────────────────────────────────
 * `actionable` arrives as an explicit prop from the page, exactly like every
 * other decision in this file. CLAUDE.md's sanctioned-exception box: *"Never
 * re-derive a permission inside `OrderTableView.js`"*. Deriving it from
 * `rowClassName` — sniffing for a colour token in a class string — would be the
 * same violation wearing a disguise, and would couple presentation to a string
 * a caller is free to change.
 */
export function ExpandChevron({ expanded, actionable = false, testId }) {
  return (
    <svg
      data-chevron
      data-testid={testId}
      // A hook for the tests and for anyone reading the DOM, so neither has to
      // parse a class string to answer "is this row actionable".
      data-actionable={actionable ? 'true' : undefined}
      xmlns="http://www.w3.org/2000/svg"
      className={`h-4 w-4 ${actionable ? ACTIONABLE_CHEVRON_CLASS : DEFAULT_CHEVRON_CLASS} transition-transform ${expanded ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

/**
 * The expanded row: a full-span `<tr>` holding the sticky, width-measured
 * panel, with the loading spinner / content / "Failed to load" fallback.
 *
 * @param {number} colSpan  how many columns the row spans (the caller's own set)
 * @param {number|null} panelWidth  the measured scrollport width, or `null`
 * @param {string} testId  `data-testid` on the spanning cell
 * @param {boolean} isLoading  the detail fetch is in flight
 * @param {*} detail  the loaded detail — falsy renders the failure line
 * @param {React.ReactNode} children  the finished expanded content
 */
// The open / close animation is `hooks/useExpandAnimation.js` (2026-10-09).
// 🔀 `open` (default `true`, so a caller that still mounts this conditionally —
// the Breakdown's phone gag panel — is unchanged): a caller that renders it
// ALWAYS and passes `open={isExpanded}` gets an eased close too. While closing
// the panel shows the LAST content it had open (the caller usually stops
// building it on collapse), so the same tree stays mounted and nothing refetches.
export function ExpandedPanelRow({
  open = true,
  colSpan,
  panelWidth,
  testId,
  isLoading,
  detail: detailProp,
  children,
}) {
  const panelRef = useRef(null);
  const lastOpen = useRef(null);
  if (open) lastOpen.current = { testId, isLoading, detail: detailProp, children };
  const present = useAnimatedPresence(open, panelRef);
  const shown = open ? { testId, isLoading, detail: detailProp, children } : (lastOpen.current || {});
  const { testId: detailsTestId, isLoading: isLoadingDetail, detail, children: expandedContent } = shown;
  useExpandAnimation(
    panelRef,
    present ? (isLoadingDetail ? 'loading' : (detail ? 'detail' : 'failed')) : '__absent__',
    { animateOnMount: true }
  );
  if (!present) return null;
  return (
    // `data-pan-skip`: the open panel — `OrderInlineEditor`'s form or the
    // read-only details — never starts a table pan, so a drag inside it
    // still selects text (`hooks/useDragScroll`).
    // `data-expanded-panel`: `ui/PageScrollTable`'s phone edge fade skips it.
    <tr onClick={(e) => e.stopPropagation()} data-pan-skip="" data-expanded-panel="">
      {/* min-w-0 lets this full-span cell shrink below its content's
          intrinsic width, so a wide panel scrolls inside its own box
          rather than stretching the cell → table → page.

          🚨 **COUNTS `ORDER_TABLE_COLUMNS`, NOT `ITEM_COLUMNS` — the
          deliberate opposite of the item-preview span above.** This cell is
          the whole row, so it must track EVERY column including the PO one
          added on 2026-08-08; the preview span must stay at the four item
          columns. Both derive, so neither is a number to maintain — but they
          derive from DIFFERENT sets and must not be unified. */}
      {/* 🔀 `columns.length`, NOT `ORDER_TABLE_COLUMNS.length`, since the
          column set became per-surface (2026-08-12). A frozen 12 on an
          eleven-column table spans one cell too many, which stretches the
          panel past the last column and can push a horizontal scrollbar onto
          a table that fits. */}
      {/* A clear gap BELOW an opened order before the next row (owner 2026-10-03, every order + hire table). */}
      <td colSpan={colSpan} className="p-0 pb-6 min-w-0" data-testid={detailsTestId}>
        {/* `sticky left-0` keeps the panel on screen while the table is
            scrolled sideways — without it the panel starts at the table's
            full width and its left edge sits off-screen the moment you
            scroll right.
            ── ⚠️ WIDTH REWORKED 2026-08-08, and the old comment here was
            already out of date when it was written. It said this was
            "the same device as GagGroupTable's pre-measure fallback,
            without the measuring machinery: this panel has no Gantt in it,
            so a viewport-capped width is enough." Two things killed that
            premise. (1) The table is now `w-full`, so on a wide screen the
            72rem cap left the panel visibly NARROWER than the row it
            belongs to — the same dead whitespace the full-width change
            exists to remove. (2) `100vw` was never the right proxy for the
            space available: the content area is the viewport minus the
            page padding minus the sidebar, which swings 12rem when the
            sidebar collapses, so on a 1366px screen with the sidebar open
            even the 72rem panel already overhung the scrollport — and an
            overhang on a `sticky left-0` element is UNREACHABLE, because
            the panel moves with the scroll instead of into view.
            So: size it to the measured scrollport (`useMeasuredWidth` in
            the shell, delivered through OrderTableWidthContext), which is
            exactly right in both bands — equal to the table when the table
            fits, equal to the visible width when it does not. The old
            expression stays as the FIRST-PAINT fallback only, for the
            render before the measurement lands and for a row rendered
            outside the shell. */}
        <div
          ref={panelRef}
          className={`sticky left-0 max-w-full ${panelWidth == null ? 'w-[min(72rem,calc(100vw_-_2rem))]' : ''} border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 p-4 max-md:px-1 max-md:py-2`}
          style={panelWidth != null ? { width: panelWidth } : undefined}
        >
          {isLoadingDetail ? (
            <div className="flex items-center justify-center py-4">
              <svg className="animate-spin h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading order details...</span>
            </div>
          ) : detail ? (
            expandedContent
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load order details.</p>
          )}
        </div>
      </td>
    </tr>
  );
}

// 🔀 2026-10-25: MOVED VERBATIM from gags/GagGroupTable.js so the Manage Budgets
// rows use the Breakdown's own ⋯ menu (owner ruling A: Delete lives in the row menu).
/**
 * Kebab (⋮) row-actions dropdown. The menu is rendered in a PORTAL to
 * document.body and positioned with position:fixed from the button's
 * getBoundingClientRect — the table lives inside a scrollport that would
 * otherwise clip an absolutely-positioned menu (last row / right edge).
 * ⚠️ **The scrollport in question CHANGED on 2026-08-08 and the portal is still
 * required — arguably more so.** It used to be this group's own
 * `overflow-x-auto` div; that is gone, and the clipper is now the ONE
 * page-level region, which owns both axes and is height-capped. So a menu on
 * the last visible row would be clipped VERTICALLY as well as horizontally now.
 * Do not "simplify" this back to an absolutely-positioned menu.
 * Closes on outside click, Escape, a scroll that MOVES the trigger, resize, and on
 * selecting an item; supports arrow/Home/End roving focus + Enter/Escape.
 *
 * Props:
 *  - items: [{ key, label, title, danger?, onClick }] — already filtered to
 *    the actions valid for this row's state + the caller's permissions.
 *  - label: accessible name for the trigger + menu.
 */
export function KebabMenu({ items, label = 'Row actions' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const MENU_W = 208; // matches w-52 (11rem is min; give a stable first guess)

  const openMenu = (e) => {
    e.stopPropagation();
    const r = btnRef.current.getBoundingClientRect();
    // First guess: below the button, right-aligned. useLayoutEffect refines
    // it with the menu's real measured size before the browser paints.
    setCoords({ top: r.bottom + 4, left: Math.max(8, r.right - MENU_W) });
    setActiveIdx(0);
    setOpen(true);
  };

  // Refine position from the menu's measured box: clamp horizontally to the
  // viewport and flip above the button when it would overflow the bottom
  // (the last-row case). Runs once per open (dep: [open]).
  useLayoutEffect(() => {
    if (!open) return;
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const r = btn.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    const pad = 8;
    let left = r.right - mw;
    left = Math.max(pad, Math.min(left, window.innerWidth - mw - pad));
    let top = r.bottom + 4;
    if (top + mh > window.innerHeight - pad && r.top - mh - 4 >= pad) top = r.top - mh - 4;
    setCoords((c) => (c && c.top === top && c.left === left ? c : { top, left }));
  }, [open]);

  // Close on outside interaction. Scroll uses capture so an inner scrollport
  // also dismisses the fixed-position menu — since 2026-08-08 that is the ONE
  // page-level region rather than a per-group horizontal scrollport, and the
  // capture phase is what makes this indifferent to which element scrolls.
  // 🔑 It matters MORE now: the region scrolls vertically, so a menu left open
  // would visibly detach from its row rather than merely from its column.
  //
  // 🔑 A SCROLL CLOSES THE MENU ONLY IF IT MOVED THE TRIGGER (2026-09-27).
  // The fixed-position menu detaches from its row only when the row moves, so
  // that — not "a scroll event arrived" — is the condition. Scroll events are
  // delivered a frame AFTER the offset changes, so a scroll that happened
  // BEFORE the menu opened (a click on a kebab the browser first scrolled into
  // view — Playwright's click does exactly that, and so can focus()) arrived
  // after this listener attached and closed the menu ~50ms after it opened.
  // Measured on this build AND on prod `c2846306`: Playwright's
  // `locator.click()` on an off-screen kebab → closed within 50ms; a real user
  // who scrolls the pane first, then clicks → stays open. The anchor is taken
  // here, after the open has painted, so any scroll already applied is in it.
  useEffect(() => {
    if (!open) return undefined;
    const rectOf = () => btnRef.current?.getBoundingClientRect();
    const anchor = rectOf();
    const close = () => {
      const now = rectOf();
      if (!anchor || !now) { setOpen(false); return; }
      if (Math.abs(now.top - anchor.top) > 0.5 || Math.abs(now.left - anchor.left) > 0.5) setOpen(false);
    };
    const onDocMouse = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDocMouse, true);
    document.addEventListener('keydown', onKey, true);
    // Resize still closes unconditionally: the menu's viewport clamp is stale.
    const closeNow = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', closeNow);
    return () => {
      document.removeEventListener('mousedown', onDocMouse, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', closeNow);
    };
  }, [open]);

  // Roving focus: move DOM focus to the active menuitem.
  useEffect(() => {
    if (open && itemRefs.current[activeIdx]) itemRefs.current[activeIdx].focus();
  }, [open, activeIdx]);

  const onMenuKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % items.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i - 1 + items.length) % items.length); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIdx(0); }
    else if (e.key === 'End') { e.preventDefault(); setActiveIdx(items.length - 1); }
  };

  const runItem = (e, item) => {
    e.stopPropagation();
    setOpen(false);
    item.onClick();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(e) => { if (open) { e.stopPropagation(); setOpen(false); } else openMenu(e); }}
        className={`inline-flex items-center justify-center h-7 w-7 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${TAP_TARGET_PHONE}`}
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 60 }}
          className="min-w-[11rem] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
        >
          {items.map((item, i) => (
            <button
              key={item.key || item.label}
              ref={(el) => { itemRefs.current[i] = el; }}
              type="button"
              role="menuitem"
              tabIndex={i === activeIdx ? 0 : -1}
              title={item.title}
              onClick={(e) => runItem(e, item)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors focus:outline-none ${
                item.danger
                  ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 focus:bg-red-50 dark:focus:bg-red-900/30'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
